import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  CLIENT_EVENT_INTENT,
  type HandshakeAuth,
  type Identity,
  type Intent,
  type Snapshot,
  type WireError,
} from 'shared';
import {
  connectionReducer,
  initialConnectionState,
  type ConnectionState,
} from './connection';
import { getAdminToken, getPlayerToken, setPlayerToken } from './storage';

export interface RoomConnection extends ConnectionState {
  roomToken: string;
  isProjector: boolean;
  send: (intent: Intent) => void;
  dismissError: () => void;
}

const Ctx = createContext<RoomConnection | null>(null);

export function useRoom(): RoomConnection {
  const value = useContext(Ctx);
  if (!value) throw new Error('useRoom must be used inside a RoomProvider');
  return value;
}

/** Convenience: the snapshot, once one has arrived. */
export function useSnapshot(): Snapshot | null {
  return useRoom().snapshot;
}

export function RoomProvider({
  roomToken,
  isProjector = false,
  children,
}: {
  roomToken: string;
  isProjector?: boolean;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(connectionReducer, initialConnectionState);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const auth: HandshakeAuth = { roomToken };
    if (isProjector) {
      // The projector presents the room token only, and can issue no intents.
      auth.projector = true;
    } else {
      const playerToken = getPlayerToken(roomToken);
      const adminToken = getAdminToken(roomToken);
      if (playerToken) auth.playerToken = playerToken;
      if (adminToken) auth.adminToken = adminToken;
    }

    dispatch({ type: 'connecting' });
    const socket = io({ auth: auth as unknown as Record<string, unknown>, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => dispatch({ type: 'connected' }));
    socket.on('disconnect', () => dispatch({ type: 'disconnected' }));
    socket.on('snapshot', (snapshot: Snapshot) => dispatch({ type: 'snapshot', snapshot }));
    socket.on('error', (error: WireError) => dispatch({ type: 'error', error }));
    socket.on('identity', (identity: Identity) => {
      setPlayerToken(roomToken, identity.playerToken);
      dispatch({ type: 'identity', playerId: identity.playerId });
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, [roomToken, isProjector]);

  const send = useCallback((intent: Intent) => {
    socketRef.current?.emit(CLIENT_EVENT_INTENT, intent);
  }, []);

  const dismissError = useCallback(() => dispatch({ type: 'dismissError' }), []);

  const value = useMemo<RoomConnection>(
    () => ({ ...state, roomToken, isProjector, send, dismissError }),
    [state, roomToken, isProjector, send, dismissError],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
